import { MouseEvent, useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import type { SupportAttachment, SupportTicketDetails } from 'chaika-frontend-types/frontend-cms';

import { getSupportErrorCode, SupportApi } from '@api/SupportApi';

import { supportKeys, supportQueryOptions } from '../queries/supportQueries';
import { applySupportRealtimeEvent } from './supportRealtimeState';
import { ComposerAttachment } from './useComposerAttachments';
import { OutgoingDraft, OutgoingDraftHandlers } from './useSupportComposer';

export interface OutgoingSupportMessage {
    attachments: ComposerAttachment[];
    clientId: string;
    createdAt: string;
    serverId?: string;
    status: 'sending' | 'failed' | 'sent';
    text: string;
    handlers: OutgoingDraftHandlers;
}

interface SendMessageVariables {
    clientId: string;
    draft: OutgoingDraft;
    handlers: OutgoingDraftHandlers;
}

interface Params {
    ticketId: string;
    unreadCount: number;
    isWatchingLatestMessages: boolean;
    onTicketNotFound?(): void;
}

const ATTACHMENT_URL_LIFETIME_BEFORE_REFRESH_MS = 50 * 60 * 1000;

export function useTicketDetail({ ticketId, unreadCount, isWatchingLatestMessages, onTicketNotFound }: Params) {
    const queryClient = useQueryClient();
    const [outgoingMessages, setOutgoingMessages] = useState<OutgoingSupportMessage[]>([]);
    const ticketQuery = useQuery(supportQueryOptions.ticket(ticketId));
    const ticket: SupportTicketDetails | undefined = ticketQuery.data;
    const isReadOnly = ticket?.status === 'deleted';
    const messagesQuery = useInfiniteQuery({
        ...supportQueryOptions.messages(ticketId),
        enabled: Boolean(ticket) && !isReadOnly,
    });
    const createMessageMutation = useMutation({
        mutationKey: [...supportKeys.messages(ticketId), 'create'],
        retry: false,
        mutationFn: ({ draft }: SendMessageVariables) =>
            SupportApi.createMessage(
                ticketId,
                draft.text,
                draft.attachments.flatMap(({ state }) => (state.kind === 'uploaded' ? [state.attachmentId] : [])),
            ),
        onSuccess: (response, { clientId }) => {
            setOutgoingMessages(currentMessages =>
                currentMessages.map(message =>
                    message.clientId === clientId
                        ? { ...message, serverId: response.id, createdAt: response.createdAt, status: 'sent' }
                        : message,
                ),
            );
            void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
            void queryClient.invalidateQueries({ queryKey: supportKeys.ticketLists() });
        },
        onError: (error, { clientId, handlers }) => {
            const errorCode = getSupportErrorCode(error);
            const removeOutgoingMessage = () =>
                setOutgoingMessages(currentMessages =>
                    currentMessages.filter(message => message.clientId !== clientId),
                );
            switch (errorCode) {
                case 'TICKET_DELETED':
                    removeOutgoingMessage();
                    applySupportRealtimeEvent(queryClient, {
                        type: 'ticket.status_changed',
                        payload: {
                            ticketId,
                            status: 'deleted',
                            statusName: ticket?.statusName ?? '',
                            statusGroup: null,
                        },
                    });
                    return;
                case 'NOT_FOUND':
                    removeOutgoingMessage();
                    void queryClient.invalidateQueries({ queryKey: supportKeys.ticketLists() });
                    onTicketNotFound?.();
                    return;
                case 'INVALID_ATTACHMENT':
                    removeOutgoingMessage();
                    handlers.onAttachmentsRejected();
                    return;
                default:
                    setOutgoingMessages(currentMessages =>
                        currentMessages.map(message =>
                            message.clientId === clientId ? { ...message, status: 'failed' } : message,
                        ),
                    );
            }
        },
    });
    const { mutate: markTicketAsRead } = useMutation({
        mutationFn: () => SupportApi.markTicketAsRead(ticketId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: supportKeys.ticketLists() }),
        onError: () => console.error('[SupportWidget] Failed to mark the open ticket as read'),
    });
    const isSendingMessage = createMessageMutation.isPending;
    const shouldMarkAsRead = Boolean(ticket) && !isReadOnly && isWatchingLatestMessages && unreadCount > 0;

    useEffect(() => {
        if (shouldMarkAsRead) markTicketAsRead();
    }, [markTicketAsRead, shouldMarkAsRead]);

    useEffect(() => {
        const serverMessageIds = new Set(
            messagesQuery.data?.pages.flatMap(page => page.items).map(message => message.id) ?? [],
        );
        if (serverMessageIds.size === 0) return;
        setOutgoingMessages(currentMessages =>
            currentMessages.filter(message => !message.serverId || !serverMessageIds.has(message.serverId)),
        );
    }, [messagesQuery.data]);

    const canStartSending = (): boolean =>
        !isReadOnly && !queryClient.isMutating({ mutationKey: [...supportKeys.messages(ticketId), 'create'] });

    const sendMessage = (draft: OutgoingDraft, handlers: OutgoingDraftHandlers): boolean => {
        if (!canStartSending()) return false;

        const clientId = uuid();
        setOutgoingMessages(currentMessages => [
            ...currentMessages,
            {
                attachments: draft.attachments,
                clientId,
                createdAt: new Date().toISOString(),
                status: 'sending',
                text: draft.text,
                handlers,
            },
        ]);
        createMessageMutation.mutate({ clientId, draft, handlers });
        return true;
    };

    const retryMessage = (clientId: string): void => {
        const message = outgoingMessages.find(candidate => candidate.clientId === clientId);
        if (message?.status !== 'failed' || !canStartSending()) return;

        setOutgoingMessages(currentMessages =>
            currentMessages.map(candidate =>
                candidate.clientId === clientId ? { ...candidate, status: 'sending' } : candidate,
            ),
        );
        createMessageMutation.mutate({
            clientId,
            draft: { text: message.text, attachments: message.attachments },
            handlers: message.handlers,
        });
    };

    const openAttachment = (event: MouseEvent<HTMLAnchorElement>, attachment: SupportAttachment): void => {
        if (Date.now() - messagesQuery.dataUpdatedAt <= ATTACHMENT_URL_LIFETIME_BEFORE_REFRESH_MS) return;

        event.preventDefault();
        const attachmentTab = window.open('', '_blank');
        void messagesQuery.refetch().then(({ data }) => {
            const refreshedAttachment = data?.pages
                .flatMap(page => page.items)
                .flatMap(message => message.attachments)
                .find(candidate => candidate.id === attachment.id);
            if (!attachmentTab) return;
            attachmentTab.opener = null;
            attachmentTab.location.href = refreshedAttachment?.url ?? attachment.url;
        });
    };

    const serverMessages = (messagesQuery.data?.pages.flatMap(page => page.items) ?? []).reverse();
    const serverMessageIds = new Set(serverMessages.map(message => message.id));

    return {
        ticket,
        messages: serverMessages,
        outgoingMessages: outgoingMessages.filter(
            message => !message.serverId || !serverMessageIds.has(message.serverId),
        ),
        isLoading: ticketQuery.isLoading || (!isReadOnly && messagesQuery.isLoading),
        error: ticketQuery.error ?? (!isReadOnly ? messagesQuery.error : undefined),
        hasOlderMessages: messagesQuery.hasNextPage,
        loadOlderMessages: messagesQuery.fetchNextPage,
        isLoadingOlderMessages: messagesQuery.isFetchingNextPage,
        sendMessage,
        retryMessage,
        openAttachment,
        refreshMessages: async () => {
            await messagesQuery.refetch();
        },
        isSendingMessage,
        isReadOnly,
    };
}
